/**
 * @module app.cm
 */

import { autoinject, bindingMode } from 'aurelia-framework';
import { ComponentBase, Sorting, StandardLoadOptions, SortDirection } from 'app/shared/lib/essette-site';
import { apiCall } from 'app/shared/lib/api/api';
import { I18N } from 'aurelia-i18n';
import { bindable } from 'aurelia-typed-observable-plugin';
import { IContactLogConfig } from 'app/core/models/essette-interfaces';
import { ContactLogService } from 'app/core/services/contact-log-service';
import { EssPagerCustomElement } from 'app/shared/elements/ess-pager';
import { SortChangeEvent } from 'app/shared/attributes/sortable-attribute';
import { openProviderSummary } from 'app/cd/components/summary/provider/provider-summary';
import { openMemberSummary } from 'app/cd/components/summary/member/member-summary';
import { openCaseSummary } from 'app/cm/components/summary/case-summary';
import * as Core from 'app/core/models/essette-api';

@autoinject()
export class ContactLogGrid extends ComponentBase {

    ////****************** Fields and Properties ************************************////
    @bindable({ defaultBindingMode: bindingMode.toView, changeHandler: 'reload' })
    public householdCaseId: string | null = null;

    @bindable({ defaultBindingMode: bindingMode.oneTime })
    public contactLogConfig: IContactLogConfig = {};

    @bindable.boolean({ defaultBindingMode: bindingMode.toView })
    public showContactLinks: boolean = true;

    protected openProviderSummary = openProviderSummary;
    protected showProviderSummaryLink: boolean = false;
    protected openMemberSummary = openMemberSummary;
    protected showMemberSummaryLink: boolean = false;
    protected showCaseSummaryLink: boolean = false;
    protected hasCaseRoute = false;
    protected openCaseSummary = openCaseSummary;
    protected contactLogs: Core.ContactLog[] = [];
    protected sorting = new Sorting();
    protected resultCount: number = 0;
    protected pageSize: number = 10;

    ////****************** Element References ***************************************////
    protected $pager: EssPagerCustomElement | any;


    ////****************** Lifecycle events (in order of execution) *****************////
    constructor(
        private readonly contactLogService: ContactLogService,
        private readonly i18n: I18N,
        ...rest: any[]) {
        super(...rest);

        // Default Sort
        this.sorting.entries = [
            {
                reference: 'contacted.dateTimeRange.start',
                direction: SortDirection.Descending
            }
        ];
    }

    protected bind(): void {
        this.checkPermissions();
        this.contactLogService.configure(this.contactLogConfig);
        this.reload();
    }


    ////****************** Events ***************************************************////


    ////****************** Functions ************************************************////
    private async checkPermissions(): Promise<void> {
        await this.accountContext.load();
        this.hasCaseRoute = this.accountContext.hasRouteAccess('cm.case');
        this.showProviderSummaryLink = this.accountContext.hasRouteAccess('cd.provider.summary');
        this.showMemberSummaryLink = this.accountContext.hasRouteAccess('cd.member.summary');
        this.showCaseSummaryLink = this.accountContext.hasRouteAccess('cm.case.summary');
    }

    public async reload(): Promise<void> {
        this.loadContactLogByHouseholdCase();
    }

    protected applySort(event: SortChangeEvent): void {
        const { property, direction } = event.detail;
        const { sorting } = this;
        if(direction === null) {
            sorting.remove(property);
        } else {
            sorting.add({
                reference: property,
                direction: direction === 'asc' ? SortDirection.Ascending : SortDirection.Descending
            });
        }
        this.reload();
    }

    protected parseDisplayString(contact: string, i18nReference?: string): string {
        const re = /\(\S*\)$/
        const index = contact.search(re);
        let contactToReturn = contact;
        if(index > 0) {
            contactToReturn = contact.substring(0, index - 1);
        }
        return i18nReference ? `${contactToReturn} (${this.i18n.tr(i18nReference)})` : contactToReturn;
    }

    ////****************** Service Calls ********************************************////

    @apiCall
    private async loadContactLogByHouseholdCase(): Promise<void> {
        if(!this.householdCaseId) return;

        const options: StandardLoadOptions = {
            sorting: this.sorting,
            top: this.pageSize,
            page: this.$pager?.page || 0,
            includeInactive: false
        };

        const includes: string[] = [
            'member',
            'method',
            'outcome',
            'contacts',
            'primaryContact',
            'contacted.byUser',
            'case'
        ];

        try {
            this.state.beginLoad();
            let response = await this.contactLogService.loadContactLogByTarget(this.householdCaseId, options, includes);
            this.contactLogs = response.results;
            this.resultCount = response.count || 0;
        }
        catch(error) {
            throw error;
        }
        finally {
            this.state.endLoad();
        }
    }
}
